import { useState, useEffect } from "react";
import {
  buscarPorPerfil,
  getProfile,
  enviarSolicitudAmistad,
  cancelarAmistad,
} from "../../services/api";
import ProfileCard from "../../components/ProfileCard";
import countriesData from "../../data/countries";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { RotateCcw } from "lucide-react";

const SearchByPerfil = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    habla: "",
    movilidad: "",
    motricidad: "",
    ver: "",
    audition: "",
    memoire: "",
    orientation: "",
    concentration: "",
    country: "",
    ciudad: "",
    comunidad: "",
    edad_min: "",
    edad_max: "",
  });

  const [userEdad, setUserEdad] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    const fetchEdad = async () => {
      try {
        const res = await getProfile();
        const edad = calcularEdad(res.data.date_of_birth);
        setUserEdad(edad);
      } catch (err) {
        console.error("Error obteniendo edad del perfil:", err);
      }
    };

    setCountries(
      countriesData.map((c) => ({
        code: c.countryCode,
        label: c.countryLabel,
        cities: c.cityCodes,
      }))
    );

    fetchEdad();
  }, []);

  useEffect(() => {
    const selected = countries.find((c) => c.code === form.country);
    setCities(selected ? selected.cities : []);
  }, [form.country, countries]);

  const calcularEdad = (fecha) => {
    if (!fecha) return null;
    const hoy = new Date();
    const cumple = new Date(fecha);
    let edad = hoy.getFullYear() - cumple.getFullYear();
    const m = hoy.getMonth() - cumple.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < cumple.getDate())) edad--;
    return edad;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "country" ? { ciudad: "" } : {}),
    }));
  };

  const handleSearch = async () => {
    setLoading(true);
    setHasSearched(true);
    try {
      const res = await buscarPorPerfil(form);
      setResults(res.data);
    } catch (err) {
      console.error("Error al buscar perfiles:", err);
      //toast.error(t("search_profile.error", "❌ Error al buscar perfiles"));
    } finally {
      setLoading(false);
    }
  };

  const handleFollowToggle = async (userId, currentStatus) => {
    try {
      if (currentStatus === "accepted") {
        await cancelarAmistad(userId);
        toast.success(t("search_profile.unfollow"));
      } else {
        await enviarSolicitudAmistad(userId);
        toast.success(t("search_profile.follow"));
      }

      const res = await buscarPorPerfil(form);
      setResults(res.data);
    } catch (err) {
      toast.error(t("search_profile.follow_error"));
      console.error("Error al modificar amistad:", err);
    }
  };

    // HAPPYCAP_SEARCH_FILTERS_MODERN_V2
  const featureIcons = {
    habla: "🗣️",
    movilidad: "🚶",
    motricidad: "🤲",
    ver: "👁️",
    audition: "👂",
    memoire: "🧠",
    orientation: "🧭",
    concentration: "🧩",
  };

  const fieldIcons = {
    country: "🌍",
    ciudad: "🏙️",
    edad_min: "🎂",
    edad_max: "🎂",
  };

  const cleanLabel = (label) =>
    String(label || "")
      .replace(/[\p{Extended_Pictographic}\uFE0F\u200D]/gu, "")
      .trim();

  const renderSelect = (
    name,
    label,
    options,
    labelKey = null,
    valueKey = null
  ) => {
    const isFeature = Object.prototype.hasOwnProperty.call(
      featureIcons,
      name
    );

    const icon = featureIcons[name] || fieldIcons[name] || "🔎";
    const title = cleanLabel(label);

    return (
      <fieldset className="min-w-0 rounded-[22px] border border-[#E3EDF4] bg-[#F7FAFD] p-4 transition-all hover:border-[#C9DFED] sm:p-5">
        <legend className="sr-only">{title}</legend>

        <div className="mb-4 flex items-center gap-3">
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[#E1EDF5] bg-white text-xl shadow-sm"
            aria-hidden="true"
          >
            {icon}
          </span>

          <span className="min-w-0 text-xs font-semibold uppercase tracking-wide text-[#648098]">
            {title}
          </span>
        </div>

        {isFeature ? (
          <div className="flex flex-wrap gap-2">
            {options.map((option) => {
              const selected = form[name] === option;

              return (
                <button
                  key={option}
                  type="button"
                  aria-pressed={selected}
                  onClick={() =>
                    handleChange({
                      target: { name, value: selected ? "" : option },
                    })
                  }
                  className={`min-h-11 max-w-full break-words rounded-xl border px-3.5 py-2.5 text-left text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#247BA8] focus-visible:ring-offset-2 ${
                    selected
                      ? "border-[#247BA8] bg-[#247BA8] text-white shadow-sm"
                      : "border-[#D8E7F0] bg-white text-[#245580] hover:border-[#247BA8] hover:bg-[#EDF7FD]"
                  }`}
                >
                  {selected && (
                    <span className="mr-1.5" aria-hidden="true">
                      ✓
                    </span>
                  )}
                  {t(`profile_options.${option}`, option)}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="relative">
            <select
              name={name}
              aria-label={title}
              value={form[name]}
              onChange={handleChange}
              className="min-h-12 w-full cursor-pointer appearance-none rounded-xl border border-[#D8E7F0] bg-white px-4 py-3 pr-11 text-[15px] font-semibold text-[#245580] outline-none transition hover:border-[#247BA8] focus:border-[#247BA8] focus:ring-2 focus:ring-[#247BA8]/15"
            >
              <option value="">{title}</option>
              {options.map((opt) => (
                <option
                  key={labelKey ? opt[valueKey] : opt}
                  value={valueKey ? opt[valueKey] : opt}
                >
                  {labelKey
                    ? opt[labelKey]
                    : typeof opt === "number"
                      ? opt
                      : t(`profile_options.${opt}`, opt)}
                </option>
              ))}
            </select>

            <span
              className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-xl text-[#247BA8]"
              aria-hidden="true"
            >
              ⌄
            </span>
          </div>
        )}
      </fieldset>
    );
  };

  const getEdadOptions = () => {
    if (userEdad === null) return [];
    return userEdad < 18
      ? Array.from({ length: 18 }, (_, i) => i)
      : Array.from({ length: 82 }, (_, i) => i + 18);
  };

  const edadOptions = getEdadOptions();
  const edadMin = parseInt(form.edad_min);
  const edadMaxOptions = edadOptions.filter((edad) =>
    isNaN(edadMin) ? true : edad >= edadMin
  );

    const resetForm = () => {
      setForm({
        habla: "",
        movilidad: "",
        motricidad: "",
        ver: "",
        audition: "",
        memoire: "",
        orientation: "",
        concentration: "",
        country: "",
        ciudad: "",
        comunidad: "",
        edad_min: "",
        edad_max: "",
      });
      setCities([]);
      setResults([]);
      setHasSearched(false);
    };


  return (
    <div className="mx-auto mt-2 md:mt-8 max-w-6xl px-4 pb-10">

        <div className="mb-5 grid grid-cols-1 gap-4 rounded-3xl border border-[#E3EDF4] bg-white p-4 shadow-sm sm:grid-cols-2 sm:p-6 lg:gap-5">
          {renderSelect(
            "habla",
            t("profile.habla"),
            ["non verbal", "verbal", "caa"]
          )}
          {renderSelect(
            "movilidad",
            t("profile.movilidad"),
            ["lit", "fauteuil roulant", "béquilles", "sans support"]
          )}
          {renderSelect(
            "motricidad",
            t("profile.motricidad"),
            ["non", "spasticité", "imprécise", "ordinaire", "mouv.anormaux"]
          )}
          {renderSelect("ver", t("profile.ver"), ["voyante", "aveugle"])}
          {renderSelect(
            "audition",
            t("profile.audition"),
            ["sans", "faible", "ordinaire", "hypersensible"]
          )}
          {renderSelect(
            "memoire",
            t("profile.memoire"),
            ["sans", "faible", "ordinaire"]
          )}
          {renderSelect(
            "orientation",
            t("profile.orientation"),
            ["sans", "faible", "ordinaire"]
          )}
          {renderSelect(
            "concentration",
            t("profile.concentration"),
            ["sans", "ephémere", "partial", "durable"]
          )}
          {renderSelect("country", t("search_profile.country"), countries, "label", "code")}
          {cities.length > 0 &&
            renderSelect("ciudad", t("search_profile.city"), cities)}

          <div className="min-w-0 rounded-[22px] border border-[#E3EDF4] bg-[#F7FAFD] p-4 transition-all hover:border-[#C9DFED] focus-within:border-[#247BA8] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#247BA8]/15 sm:p-5">
            <label
              htmlFor="search-community"
              className="mb-4 flex items-center gap-3"
            >
              <span
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[#E1EDF5] bg-white text-xl shadow-sm"
                aria-hidden="true"
              >
                🏘️
              </span>

              <span className="text-xs font-semibold uppercase tracking-wide text-[#648098]">
                {cleanLabel(t("search_profile.community"))}
              </span>
            </label>

            <input
              id="search-community"
              type="text"
              name="comunidad"
              placeholder={cleanLabel(t("search_profile.community"))}
              value={form.comunidad}
              onChange={handleChange}
              className="min-h-12 w-full rounded-xl border border-[#D8E7F0] bg-white px-4 py-3 text-[15px] font-semibold text-[#245580] outline-none transition placeholder:font-normal placeholder:text-slate-400 hover:border-[#247BA8] focus:border-[#247BA8] focus:ring-2 focus:ring-[#247BA8]/15"
            />
          </div>

          {renderSelect("edad_min", t("search_profile.age_min"), edadOptions)}
          {renderSelect("edad_max", t("search_profile.age_max"), edadMaxOptions)}
        </div>


        <div className="flex items-center gap-3">
          <button
            onClick={handleSearch}
            disabled={loading}
            className="rounded-full bg-[#16A34A] px-7 py-2.5 text-[15px] font-semibold text-white shadow-sm transition hover:bg-[#15803D] hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading
              ? t("search_profile.searching", "Buscando...")
              : t("search_profile.search", "Buscar")}
          </button>

          <button
            onClick={resetForm}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-[#247BA8] shadow-sm transition hover:border-[#B8D8EA] hover:bg-[#EFF7FC]"
            title={t("search_profile.reset", "Resetear filtros")}
          >
            <RotateCcw className="h-4 w-4 text-[#247BA8]" />
          </button>
        </div>


      {hasSearched && !loading && results.length === 0 && (
        <p className="text-center text-gray-500 mt-8">
          {t("search_profile.no_results", "❌ No hay resultados para las características seleccionadas.")}
        </p>
      )}

      {results.length > 0 && (
        <div className="mt-8">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {results.map((profile) => (
              <ProfileCard
                key={profile.id}
                profile={profile}
                onFollowToggle={handleFollowToggle}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchByPerfil;
